import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useHelp } from '../../context/HelpContext';

const PAGES = [
    { id: 'level-overview', label: { fr: 'Aperçu du niveau (+ navigation)', en: 'Level Overview (+ navigation)' } },
    { id: 'level-selector', label: { fr: 'Sélecteur de niveaux', en: 'Level Selector' } },
    { id: 'exam-session', label: { fr: 'Session d\'examen', en: 'Exam Session' } },
    { id: 'quiz-session', label: { fr: 'Session de quiz', en: 'Quiz Session' } },
    { id: 'course-modal', label: { fr: 'Lecteur de cours', en: 'Course Player' } },
    { id: 'stats-tab', label: { fr: 'Statistiques', en: 'Statistics' } },
    { id: 'custom-tab', label: { fr: 'Sur mesure', en: 'Custom' } },
    { id: 'courses-tab', label: { fr: 'Onglet Cours', en: 'Courses Tab' } },
    { id: 'account', label: { fr: 'Espace Compte', en: 'Account Space' } },
    { id: 'order-history', label: { fr: 'Mes achats', en: 'My purchases' } },
];

const ToggleRow = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white">{label}</p>
            {description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            )}
        </div>
        <button
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
        >
            <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                    checked ? 'translate-x-5' : 'translate-x-0'
                }`}
            />
        </button>
    </div>
);

// ─────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────
const HelpSettings = () => {
    const { language } = useLanguage();
    const {
        helpPrefs,
        setGlobalEnabled,
        setAutoTrigger,
        setShowTriggerButton,
        resetAllSeen,
        resetPageSeen,
        hasSeenPage,
    } = useHelp();

    return (
        <div className="space-y-8 pb-12">
            {/* Banner card */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#880e4f] via-[#4a148c] to-[#311b92] border border-white/5 !mb-8 shadow-xl text-white">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }}
                />
                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white/20 rounded-2xl">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
                                <path d="M4.93 4.93l4.24 4.24" /><path d="M14.83 14.83l4.24 4.24" />
                                <path d="M14.83 9.17l4.24-4.24" /><path d="M4.93 19.07l4.24-4.24" />
                            </svg>
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">{language === 'fr' ? 'Tutoriel' : 'Tutorial'}</h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-sm leading-snug">
                        {language === 'fr'
                            ? 'Configuration de la visite guidée.'
                            : 'Guided tour settings.'}
                    </p>
                </div>
                <div className="absolute right-[-40px] bottom-[-40px] opacity-20 pointer-events-none">
                    <svg className="w-72 h-72 text-white stroke-[0.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
                        <path d="M4.93 4.93l4.24 4.24" /><path d="M14.83 14.83l4.24 4.24" />
                        <path d="M14.83 9.17l4.24-4.24" /><path d="M4.93 19.07l4.24-4.24" />
                    </svg>
                </div>
            </div>

            {/* ============ Guide configuration ============ */}
            <div>
                <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight mb-4">
                    {language === 'fr' ? 'Configuration du guide' : 'Guide settings'}
                </h2>

                <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 space-y-5">
                    <ToggleRow
                        label={language === 'fr' ? 'Activer le guide contextuel' : 'Enable contextual guide'}
                        description={language === 'fr'
                            ? 'Active le système de guide et le bouton de visite guidée.'
                            : 'Enables the guide system and guided tour button.'}
                        checked={helpPrefs.globalEnabled}
                        onChange={setGlobalEnabled}
                    />
                    <div className="border-t border-gray-100 dark:border-white/5" />
                    <ToggleRow
                        label={language === 'fr' ? 'Visite guidée automatique' : 'Automatic guided tour'}
                        description={language === 'fr'
                            ? 'Lance automatiquement la visite lors de ta première visite sur chaque page.'
                            : 'Automatically starts the tour on your first visit to each page.'}
                        checked={helpPrefs.autoTrigger}
                        onChange={setAutoTrigger}
                    />
                    <div className="border-t border-gray-100 dark:border-white/5" />
                    <ToggleRow
                        label={language === 'fr' ? 'Afficher le bouton « ? Guide »' : 'Show "? Guide" button'}
                        description={language === 'fr'
                            ? 'Bouton flottant en bas à droite pour relancer la visite guidée.'
                            : 'Floating button in the bottom-right corner to restart the guided tour.'}
                        checked={helpPrefs.showTriggerButton}
                        onChange={setShowTriggerButton}
                    />
                </div>
            </div>

            {/* Reset all */}
            <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Réinitialiser toutes les visites' : 'Reset all tours'}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {language === 'fr'
                                ? 'Relance la visite guidée sur toutes les pages lors de ta prochaine visite.'
                                : 'Restarts the guided tour on all pages on your next visit.'}
                        </p>
                    </div>
                    <button
                        onClick={resetAllSeen}
                        className="px-4 py-2 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-500/20 transition flex-shrink-0"
                    >
                        {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </button>
                </div>
            </div>

            {/* Per-page status list */}
            <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-white/5">
                    <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        {language === 'fr' ? 'Pages visitées' : 'Visited pages'}
                    </h3>
                </div>
                {PAGES.map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/5 last:border-b-0">
                        <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${hasSeenPage(page.id) ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{page.label[language]}</span>
                        </div>
                        {hasSeenPage(page.id) && (
                            <button
                                onClick={() => resetPageSeen(page.id)}
                                className="text-[10px] font-bold text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                            >
                                {language === 'fr' ? 'Relancer' : 'Restart'}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HelpSettings;
